import { createFileRoute } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import {
  FaqAccordion,
  FeatureBlock,
  Hero,
  QuoteTestimonials,
  TextSection,
} from '~/components/sections';
import { businessFaq } from '~/content/business';
import { ButtonLink, SmartImage } from '~/components/ui';
import { getTestimonials } from '~/lib/payload';

/** Loader payload shared by the English and Arabic business routes. */
export type BusinessQuotes = Awaited<ReturnType<typeof getTestimonials>>;
import { faqPageJsonLd, pageHeadMeta } from '~/lib/seo';
import { useContent } from '~/lib/useLanguage';
import heroCss from '~/styles/hero.css?inline';

export const Route = createFileRoute('/business')({
  loader: () => getTestimonials({ data: 'business' }),
  head: () => {
    const head = pageHeadMeta({
      title: 'Global Payroll Without Monthly or Transfer Fees',
      description:
        'Pay contractors and employees worldwide from your nsave business account. Apply for team payments, request a demo, or join the general business account waitlist.',
      path: '/business',
      inlineStyles: [heroCss],
    });
    return { ...head, scripts: [...head.scripts, faqPageJsonLd(businessFaq)] };
  },
  component: BusinessPage,
});

function BusinessPage() {
  return <BusinessPageContent quotes={Route.useLoaderData()} />;
}

/**
 * Page body, parameterized by its loader data.
 *
 * Deliberately does NOT call `Route.useLoaderData()` itself: the Arabic route
 * (`/ar/business`) renders this same component, and reading the `/business` route's
 * loader from a different active match throws
 * "Could not find an active match from /business". Each route reads its own loader
 * and passes the result in.
 */
export function BusinessPageContent({ quotes }: { quotes: BusinessQuotes }) {
  const { business, shared, ui } = useContent();
  const {
    businessHero,
    businessTrust,
    businessPaths,
    businessReceive,
    businessGlobalTeam,
    businessInvoices,
    businessClosing,
    businessFaq,
  } = business;
  const waitlistPath = businessPaths.items[0]!;

  return (
    <PageShell>
      <Hero content={businessHero} statsClassName="!mt-20 md:!mt-24" />
      <TextSection content={businessTrust} />
      <div id="how-it-works" className="scroll-mt-24">
        <FeatureBlock content={businessReceive} imagePosition="left" stickyImage />
      </div>
      <FeatureBlock content={businessGlobalTeam} imagePosition="right" stickyImage />
      <FeatureBlock content={businessInvoices} imagePosition="left" stickyImage />
      <TextSection content={shared.safeSpaceSection} sectionClassName="!my-0" />

      <QuoteTestimonials title={ui.business.testimonialsTitle} quotes={quotes} />

      <FaqAccordion content={businessFaq} sectionClassName="!pt-16 !pb-16 md:!pt-24 md:!pb-24" />

      <Section className="py-12 md:py-16">
        <div className="relative min-h-[460px] overflow-hidden md:min-h-[520px]">
          <SmartImage image={businessClosing.image} className="absolute inset-0 size-full" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent rtl:bg-gradient-to-l" />
          <div className="relative z-10 flex min-h-[460px] max-w-2xl flex-col justify-end p-7 text-white md:min-h-[520px] md:p-12 lg:p-14">
            <h2 className="text-h3 font-semibold tracking-[-0.03em] text-balance md:text-h2">
              {businessClosing.title}
            </h2>
            <p className="mt-5 max-w-xl text-body whitespace-pre-line text-white/85">
              {businessClosing.body}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <ButtonLink
                href={businessClosing.primaryCta.href}
                external={businessClosing.primaryCta.external}
                size="md">
                {businessClosing.primaryCta.label}
              </ButtonLink>
              <ButtonLink
                href={businessClosing.secondaryCta.href}
                external={businessClosing.secondaryCta.external}
                variant="outline"
                size="md"
                className="border-white bg-white text-ink hover:bg-grey-50">
                {businessClosing.secondaryCta.label}
              </ButtonLink>
            </div>
          </div>
        </div>
      </Section>

      <Section className="pt-4 pb-20 md:pt-8 md:pb-28">
        <div className="border-y border-black-6 py-10 md:py-14 lg:grid lg:grid-cols-9 lg:items-center lg:gap-5">
          <div className="lg:col-span-4">
            <p className="text-body-s font-semibold tracking-wide text-red-500 uppercase">
              {waitlistPath.title}
            </p>
            <h2 className="mt-4 max-w-md text-h4 font-semibold tracking-[-0.02em] text-balance md:text-h3">
              {businessPaths.title}
            </h2>
          </div>
          <div className="mt-8 lg:col-span-4 lg:col-start-6 lg:mt-0">
            <p className="max-w-xl text-body text-grey-900">{waitlistPath.description}</p>
            <ButtonLink
              href={waitlistPath.cta.href}
              external={waitlistPath.cta.external}
              size="md"
              className="mt-6">
              {waitlistPath.cta.label}
            </ButtonLink>
          </div>
        </div>
      </Section>
    </PageShell>
  );
}
